import {
    AutoComplete,
    Checkbox,
    DatePicker,
    Form,
    Input,
    message,
    Modal,
    Collapse,
    Button,
    Tooltip,
    Upload,
} from "antd";
import Title from "antd/es/typography/Title";
import React, { useEffect, useMemo, useState } from "react";
import { HttpService } from "../../../services/http/Http.service";
import { Person } from "../../../model/models";
import dayjs from "dayjs";
import { HttpErrorService } from "../../../services/http/HttpError.service";
import HeirAddressForm from "./components/HeirAddressForm";
import { InboxOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import CommonFormFields from "./components/CommonFormFields";

const { Dragger } = Upload;
const { Panel } = Collapse;

interface Props {
    visible: boolean;
    onClose: () => void;
    simulatorId?: number;
    person: Person;
    refreshPractices?: (() => void) | undefined;
    refreshPerson?: any;
    installmentsCount?: number;
    protocolNumber?: string,
}

export default function HeirModal({
                                      visible,
                                      onClose,
                                      simulatorId,
                                      person,
                                      refreshPractices,
                                      refreshPerson,
                                      installmentsCount = 0,
                                      protocolNumber
                                  }: Readonly<Props>) {
    const [form] = Form.useForm();
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [activePanel, setActivePanel] = useState<string[]>(["0"]);


    const [autodenunciaFiles, setAutodenunciaFiles] = useState<
        Record<number, UploadFile[]>
        >({});

    const heirsWatch = Form.useWatch("heirs", form) as any[] | undefined;
    const heirsCount = heirsWatch?.length ?? 0;


    const canShowAutodenuncia = installmentsCount === 1 && heirsCount === 1;


    const someAutodenunciaChecked = useMemo(
        () => (heirsWatch ?? []).some((h) => !!h?.autodenuncia),
        [heirsWatch]
    );

    const isLocked = (rowIndex: number) =>
        !!form.getFieldValue(["heirs", rowIndex, "__locked"]);

    const handleSearch = async (value: string) => {
        if (!value || value.length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            const response = await HttpService.getInstanceAxios().get(
                `/person/searchBy?userInput=${value}`
            );
            setSearchResults(response.data?.content || []);
        } catch (error) {
            HttpErrorService.handle(
                "Errore durante la ricerca dell'utente",
                error,
                true
            );
        }
    };

    const handleSubmitAllHeirs = async () => {
        try {
            setUploading(true);

            const allValues = await form.validateFields();
            const heirsData = (allValues.heirs || []) as any[];

            //  autodenuncia allowed only when exactly 1 heir AND installmentsCount === 1
            const allowAuto = installmentsCount === 1 && heirsData.length === 1;


            const newSuccessorsList = heirsData.map((heir: any) => ({
                firstName: heir.name,
                lastName: heir.surname,
                placeOfBirth: heir.placeOfBirth,
                dateOfBirth: heir?.dateOfBirth?.format?.("YYYY-MM-DD"),
                fiscalCode: heir.fiscalCode,
                residenceProvince: heir.province,
                residenceCity: heir.city,
                residencePostalCode: heir.cap,
                residenceAddress: heir.address,
                residenceHouseNumber: heir.houseNumber,
            }));


            let rootAutoDenuncio = false;
            let rootEmail: string | undefined;
            let rootRequestProtocolNumber: string | undefined;
            let rootRequestProtocolDate: string | undefined;


            let autFiles: UploadFile[] = [];

            if (allowAuto && heirsData[0]?.autodenuncia) {
                rootAutoDenuncio = true;
                rootRequestProtocolNumber = allValues.requestProtocolNumber;
                rootRequestProtocolDate =
                    allValues.requestProtocolDate?.format?.("YYYY-MM-DD");

                autFiles = autodenunciaFiles[0] ?? [];
            }

            const transmissionType = allValues.transmissionType;
            const email = allValues.email;

            if (transmissionType === "email") rootEmail = `email: ${email}`;
            else if (transmissionType === "pec") rootEmail = `PEC: ${email}`;
            else rootEmail = email;

            const payload = {
                simulatorId,
                previousPersonId: person?.id,
                newSuccessors: newSuccessorsList,
                // root autodenuncia fields:
                autoDenuncio: rootAutoDenuncio,
                email: rootEmail,
                requestProtocolNumber: rootRequestProtocolNumber,
                requestProtocolDate: rootRequestProtocolDate,
                archiveNumber:protocolNumber
            };

            if (rootAutoDenuncio) {
                const formData = new FormData();
                formData.append(
                    "request",
                    new Blob([JSON.stringify(payload)], { type: "application/json" })
                );
                autFiles.forEach((f) => {
                    if (f?.originFileObj) formData.append("file", f.originFileObj);
                });
                await HttpService.getInstanceAxios().post("/succession", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                await HttpService.getInstanceAxios().post("/succession", payload);
            }

            message.success("Eredi salvati correttamente!");
            refreshPractices?.();
            refreshPerson?.();
            form.resetFields();
            setSearchResults([]);
            setActivePanel(["0"]);
            setAutodenunciaFiles({});
            onClose();
        } catch (error) {
            HttpErrorService.handle(
                "Errore durante il salvataggio degli eredi",
                error,
                true
            );
        }
        setUploading(false);
    };

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setSearchResults([]);
            setActivePanel(["0"]);
            setAutodenunciaFiles({});
        }
    }, [visible, form]);

    const handlePanelChange = (keys: string | string[]) => {
        if (Array.isArray(keys)) setActivePanel(keys.map(String));
        else setActivePanel([String(keys)]);
    };

    return (
        <>
            <Modal
                title="Deceduto con eredi"
                open={visible}
                onCancel={onClose}
                onOk={handleSubmitAllHeirs}
                okText="Emetti nuovo avviso di pagamento"
                maskClosable={false}
                width={750}
            >
                <Title level={5}>Anagrafica eredi</Title>

                <Form form={form} layout="vertical" name="heir-form-list">
                    <Form.List name="heirs" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                            <>
                                <Collapse
                                    accordion
                                    activeKey={activePanel}
                                    onChange={handlePanelChange}
                                >
                                    {fields.map(({ key, name }, index) => {
                                        const headerTitle = (() => {
                                            const hn = form.getFieldValue(["heirs", name, "name"]);
                                            const hs = form.getFieldValue(["heirs", name, "surname"]);
                                            return hn && hs ? `${hn} ${hs}` : `Erede #${index + 1}`;
                                        })();

                                        return (
                                            <Panel header={headerTitle} key={String(name)}>
                                                <div
                                                    style={{ display: "flex", justifyContent: "flex-end" }}
                                                >
                                                    {fields.length > 1 ? (
                                                        <Button
                                                            type="primary"
                                                            danger
                                                            onClick={() => remove(name)}
                                                        >
                                                            Rimuovi Erede
                                                        </Button>
                                                    ) : null}
                                                </div>

                                                <Form.Item
                                                    label="Codice fiscale"
                                                    name={[name, "fiscalCode"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Inserisci il codice fiscale!",
                                                        },
                                                        {
                                                            pattern:
                                                                /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$|([0-9]{11})$/i,
                                                            message: "Il codice fiscale non è valido",
                                                        },
                                                    ]}
                                                >
                                                    <AutoComplete
                                                        placeholder="Inserisci o cerca il codice fiscale"
                                                        onSearch={handleSearch}
                                                        onSelect={(value) => {
                                                            const selected = searchResults.find(
                                                                (p) => p.fiscalCode === value
                                                            );
                                                            if (selected) {
                                                                form.setFields([
                                                                    {
                                                                        name: ["heirs", name, "fiscalCode"],
                                                                        value: selected.fiscalCode,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "name"],
                                                                        value: selected.firstName,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "surname"],
                                                                        value: selected.lastName,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "placeOfBirth"],
                                                                        value: selected.placeOfBirth,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "dateOfBirth"],
                                                                        value: dayjs(selected.dateOfBirth),
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "__locked"],
                                                                        value: true,
                                                                    },
                                                                ]);
                                                            }
                                                        }}
                                                        onChange={(value) => {
                                                            const matched = searchResults.some(
                                                                (p) => p.fiscalCode === value
                                                            );
                                                            if (!matched) {
                                                                form.setFields([
                                                                    {
                                                                        name: ["heirs", name, "__locked"],
                                                                        value: false,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "name"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "surname"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "placeOfBirth"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "dateOfBirth"],
                                                                        value: null,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "province"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "city"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "cap"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "address"],
                                                                        value: undefined,
                                                                    },
                                                                    {
                                                                        name: ["heirs", name, "houseNumber"],
                                                                        value: undefined,
                                                                    },
                                                                ]);
                                                            }
                                                        }}
                                                        onClear={() => {
                                                            form.setFields([
                                                                {
                                                                    name: ["heirs", name, "__locked"],
                                                                    value: false,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "name"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "surname"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "placeOfBirth"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "dateOfBirth"],
                                                                    value: null,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "province"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "city"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "cap"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "address"],
                                                                    value: undefined,
                                                                },
                                                                {
                                                                    name: ["heirs", name, "houseNumber"],
                                                                    value: undefined,
                                                                },
                                                            ]);
                                                        }}
                                                        options={searchResults.map((person) => ({
                                                            value: person.fiscalCode,
                                                            label: `${person.fiscalCode} - ${person.firstName} ${person.lastName}`,
                                                        }))}
                                                        allowClear
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Nome"
                                                    name={[name, "name"]}
                                                    rules={[
                                                        { required: true, message: "Inserisci il nome!" },
                                                    ]}
                                                >
                                                    <Input disabled={isLocked(name)} />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Cognome"
                                                    name={[name, "surname"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Inserisci il cognome!",
                                                        },
                                                    ]}
                                                >
                                                    <Input disabled={isLocked(name)} />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Luogo di nascita"
                                                    name={[name, "placeOfBirth"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Inserisci il luogo di nascita!",
                                                        },
                                                    ]}
                                                >
                                                    <Input disabled={isLocked(name)} />
                                                </Form.Item>

                                                <Form.Item
                                                    label="Data di nascita"
                                                    name={[name, "dateOfBirth"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Inserisci la data di nascita!",
                                                        },
                                                    ]}
                                                >
                                                    <DatePicker
                                                        format="DD/MM/YYYY"
                                                        disabled={isLocked(name)}
                                                        style={{ width: "100%" }}
                                                    />
                                                </Form.Item>

                                                <HeirAddressForm form={form} name={name} />


                                                {canShowAutodenuncia && (
                                                    <Form.Item
                                                        name={[name, "autodenuncia"]}
                                                        valuePropName="checked"
                                                    >
                                                        <Checkbox>Autodenuncia</Checkbox>
                                                    </Form.Item>
                                                )}
                                            </Panel>
                                        );
                                    })}
                                </Collapse>


                                <Form.Item>
                                    <Tooltip
                                        title={
                                            canShowAutodenuncia && someAutodenunciaChecked
                                                ? "Hai inserito l’autodenuncia: puoi indicare un solo erede"
                                                : ""
                                        }
                                    >
                                        <Button
                                            type="dashed"
                                            onClick={() => {
                                                const newIndex = String(fields.length);
                                                add();
                                                setActivePanel([newIndex]);
                                            }}
                                            block
                                            icon={<PlusOutlined />}
                                            style={{ marginTop: "10px" }}
                                            disabled={canShowAutodenuncia && someAutodenunciaChecked}
                                        >
                                            Aggiungi erede
                                        </Button>
                                    </Tooltip>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <CommonFormFields fieldsToShow={["email"]} />

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const onlyHeirChecked =
                                (getFieldValue(["heirs", 0, "autodenuncia"]) ?? false) &&
                                canShowAutodenuncia;

                            if (!onlyHeirChecked) return null;

                            const fileList = autodenunciaFiles[0] ?? [];
                            return (
                                <>
                                    <Form.Item
                                        label="Carica nota di autodenuncia"
                                        name="autodenunciaRequest"
                                        rules={[
                                            {
                                                required: true,
                                                message: "Carica la nota di autodenuncia!",
                                            },
                                        ]}
                                    >
                                        <Dragger
                                            multiple={false}
                                            beforeUpload={() => false}
                                            onChange={({ fileList: fl }) =>
                                                setAutodenunciaFiles((prev) => ({ ...prev, 0: fl }))
                                            }
                                            fileList={fileList}
                                            onRemove={(file) => {
                                                setAutodenunciaFiles((prev) => {
                                                    const next = (prev[0] ?? []).filter(
                                                        (f) => f.uid !== file.uid
                                                    );
                                                    return { ...prev, 0: next };
                                                });
                                                return true;
                                            }}
                                        >
                                            <p className="ant-upload-drag-icon">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text">
                                                Clicca o trascina uno o più file
                                            </p>
                                        </Dragger>
                                    </Form.Item>


                                    <CommonFormFields
                                        fieldsToShow={[
                                            "requestProtocolNumber",
                                            "requestProtocolDate",
                                        ]}
                                    />
                                </>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>

            <CustomSpinner
                message={"Generazione PagoPa in corso..."}
                open={uploading}
            />
        </>
    );
}
